/**CORE LIBRARIES IMPORT */
import React, { useState } from "react";
import { Link } from "react-router-dom";

/**COMPONENTS IMPORT */
import TurnstileWidget, { TICKET_RESEND_ACTION } from "../Components/TurnstileWidget";

/**DATA / HOOKS / SERVICES IMPORT */
import useSiteSettings from "../hooks/useSiteSettings";
import usePageTitle from "../hooks/usePageTitle";
import { PRIVACY_PATH } from "../data/legal";
import { turnstileSiteKey, requestTicketEmail, bookingErrorMessage, getBookingMode } from "../services/booking";

/**
 * Customers who lost their confirmation email can have the ticket sent again.
 * The QR is never shown here: it only goes to the email address on the booking,
 * so knowing a booking reference is not enough to obtain a ticket.
 */
const TicketResend = () => {
  usePageTitle("Resend my tickets");
  const settings = useSiteSettings();
  const [form, setForm] = useState({ reference: "", email: "", website: "" });
  const [state, setState] = useState({ status: "idle", error: null, message: null });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const available = getBookingMode() === "backend";
  const needsTurnstile = Boolean(turnstileSiteKey);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === "sending") return;
    if (!/^GW-[A-Z0-9]{6,}$/i.test(form.reference.trim()) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setState({ status: "idle", error: "Please enter your booking reference (for example GW-1A2B3C4D) and the email address you booked with.", message: null });
      return;
    }
    if (needsTurnstile && !turnstileToken) {
      setState({ status: "idle", error: "Please complete the security check.", message: null });
      return;
    }
    const token = turnstileToken;
    setTurnstileToken(null);
    setState({ status: "sending", error: null, message: null });
    try {
      const res = await requestTicketEmail({ ...form, turnstileToken: token });
      setState({ status: "sent", error: null, message: res.message });
    } catch (err) {
      setState({ status: "idle", error: bookingErrorMessage(err), message: null });
      setTurnstileReset((n) => n + 1);
    }
  };

  return (
    <main className="simple-page enquiry-page">
      <p className="page-eyebrow">Tickets</p>
      <h1 className="page-title">Resend my tickets</h1>
      <p className="section-text">
        Your tickets (one QR code per guest) are in your booking confirmation email. If you can't find them,
        enter your booking reference and the email address you booked with, and we will send them again to that address.
      </p>

      {!available ? (
        <p className="section-text">
          Please email us at <a href={`mailto:${settings.email}`}>{settings.email}</a>.
        </p>
      ) : state.status === "sent" ? (
        <div className="enquiry-sent" role="status" aria-live="polite">
          <p className="enquiry-event-title">Request received</p>
          <p className="section-text">{state.message}</p>
          <p className="section-text">
            Nothing arrived after a few minutes? Check your spam folder, or email{" "}
            <a href={`mailto:${settings.email}`}>{settings.email}</a>.
          </p>
        </div>
      ) : (
        <form className="enquiry-form" onSubmit={submit} noValidate aria-busy={state.status === "sending"}>
          <div className="form-field">
            <label htmlFor="tr-reference">Booking reference <span aria-hidden="true">*</span></label>
            <input id="tr-reference" name="reference" type="text" autoComplete="off" autoCapitalize="characters" spellCheck="false" required maxLength={40} placeholder="GW-…" value={form.reference} onChange={update} />
          </div>
          <div className="form-field">
            <label htmlFor="tr-email">Email used for the booking <span aria-hidden="true">*</span></label>
            <input id="tr-email" name="email" type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={update} />
          </div>
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="tr-website">Website</label>
            <input id="tr-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update} />
          </div>
          <p className="form-hint form-field-full">
            For your security we never show tickets on this page. See our{" "}
            <Link to={PRIVACY_PATH} target="_blank" rel="noopener">Privacy Policy</Link>.
          </p>
          {needsTurnstile && (
            <div className="form-field-full">
              <TurnstileWidget siteKey={turnstileSiteKey} action={TICKET_RESEND_ACTION} label="Security check:" resetKey={turnstileReset} onToken={setTurnstileToken} />
            </div>
          )}
          {state.error && <p className="checkout-error form-field-full" role="alert">{state.error}</p>}
          <div className="form-field-full">
            <button type="submit" className="hero-btn hero-btn-active" disabled={state.status === "sending" || (needsTurnstile && !turnstileToken)}>
              {state.status === "sending" ? "Sending…" : "Email my tickets"}
            </button>
          </div>
        </form>
      )}
    </main>
  );
};

export default TicketResend;
